import React, { useState } from 'react';
import styles from './Rating.module.css';

export type RatingValue = 'up' | 'down' | null;

type RatingProps = {
  value?: RatingValue;
  feedbackValue?: string;
  onChange?: (value: RatingValue) => void;
  onFeedbackChange?: (message: string) => void;
  /** Flatten into parent response card (no nested shell). */
  embedded?: boolean;
};

const ThumbUpIcon = () => (
  <svg
    className={styles.icon}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    preserveAspectRatio="xMidYMid meet"
  >
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
  </svg>
);

const ThumbDownIcon = () => (
  <svg
    className={styles.icon}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    preserveAspectRatio="xMidYMid meet"
  >
    <path d="M17 14V2" />
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
  </svg>
);

const Rating: React.FC<RatingProps> = ({
  value,
  feedbackValue,
  onChange,
  onFeedbackChange,
  embedded = false,
}) => {
  const [feedback, setFeedback] = useState(feedbackValue || '');

  const handleClick = (newValue: RatingValue) => {
    const nextValue = value === newValue ? null : newValue;

    if (nextValue !== 'down') {
      setFeedback('');
      onFeedbackChange?.('');
    }

    onChange?.(nextValue);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(e.target.value);
  };

  const lastSentRef = React.useRef('');

  const handleFeedbackBlur = () => {
    const trimmed = feedback.trim();
    if (trimmed === lastSentRef.current) return;

    lastSentRef.current = trimmed;
    onFeedbackChange?.(trimmed);
  };

  return (
    <div
      className={`${styles.rating} ${embedded ? styles.embedded : ''}`}
      data-testid="analyse-message-rating"
    >
      <div className={embedded ? styles.embeddedBody : styles.shell}>
        <div className={styles.labelRow}>
          <span className={styles.spark} aria-hidden="true">
            <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
              <path d="M8 0l1.2 4.8L14 6l-4.8 1.2L8 12l-1.2-4.8L2 6l4.8-1.2L8 0zm5.5 9.5l.7 2.8 2.8.7-2.8.7-.7 2.8-.7-2.8-2.8-.7 2.8-.7.7-2.8z" />
            </svg>
          </span>
          <span className={styles.label}>Was this helpful?</span>
          <span className={styles.helperText}>
            Your feedback helps inform future responses
          </span>
        </div>

        <div className={styles.row}>
          <div className={styles.actions} role="group" aria-label="Rate this response">
            <button
              type="button"
              className={`${styles.button} ${styles.up} ${value === 'up' ? styles.activeUp : ''}`}
              onClick={() => handleClick('up')}
              aria-label="Thumbs up"
              aria-pressed={value === 'up'}
            >
              <ThumbUpIcon />
            </button>

            <button
              type="button"
              className={`${styles.button} ${styles.down} ${value === 'down' ? styles.activeDown : ''}`}
              onClick={() => handleClick('down')}
              aria-label="Thumbs down"
              aria-pressed={value === 'down'}
            >
              <ThumbDownIcon />
            </button>
          </div>

          {value === 'down' && (
            <div className={styles.inlineFeedback}>
              <input
                type="text"
                className={styles.input}
                placeholder="Tell Mia what to improve…"
                value={feedback}
                onChange={handleFeedbackChange}
                onBlur={handleFeedbackBlur}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rating;
